import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import { KEY_CODES } from '@utils';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledJobsSection = styled.section`
  max-width: 900px;
  margin: 0 auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .numbered-heading {
    margin-bottom: 20px !important;
  }

  @media (min-width: 769px) {
    padding: 0 0 100px 0 !important;
  }

  @media (max-width: 768px) {
    min-height: 100vh !important;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-bottom: 100px !important;
  }

  @media (max-width: 1080px) and (orientation: landscape), (max-height: 600px) {
    min-height: auto;
  }

  /* Using global .numbered-heading styles */

  .inner {
    display: flex;
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 40px;
    box-shadow: 0 10px 30px -15px rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 42, 42, 0.85);
      box-shadow: 0 0 30px 2px rgba(255, 42, 42, 0.35), 0 20px 40px -15px rgba(0, 0, 0, 0.6);
    }

    @media (max-width: 768px) {
      display: block;
      padding: 28px 20px !important;
      border-radius: 20px !important;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.04) 0%,
        rgba(255, 255, 255, 0.01) 100%
      ) !important;
      border: 1px solid rgba(255, 255, 255, 0.08) !important;
      backdrop-filter: blur(16px) !important;
      -webkit-backdrop-filter: blur(16px) !important;
      box-shadow: 0 15px 35px -20px rgba(0, 0, 0, 0.6) !important;

      &:hover {
        border-color: rgba(255, 42, 42, 0.85) !important;
      }
    }
  }
`;

const StyledTabList = styled.div`
  position: relative;
  z-index: 3;
  width: max-content;
  padding: 0;
  margin: 0;
  list-style: none;

  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    width: calc(100% + 40px);
    margin-left: -20px;
    padding-left: 20px;
    margin-bottom: 30px;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const StyledTabButton = styled.button`
  ${({ theme }) => theme.mixins.link};
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--tab-height);
  padding: 0 20px 2px;
  border-left: 2px solid rgba(255, 255, 255, 0.05);
  background-color: ${({ isActive }) => (isActive ? 'rgba(255, 42, 42, 0.12)' : 'transparent')};
  color: ${({ isActive }) => (isActive ? 'var(--green)' : 'var(--slate)')};
  font-family: var(--font-mono);
  font-size: var(--fz-sm);
  text-align: left;
  white-space: nowrap;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 0 15px;
    border-left: 0;
    border-bottom: 2px solid rgba(255, 255, 255, 0.05);
    text-align: center;
    background-color: ${({ isActive }) => (isActive ? 'rgba(255, 42, 42, 0.12)' : 'transparent')};
  }

  &:hover,
  &:focus {
    background-color: rgba(255, 42, 42, 0.18);
    color: var(--green);
  }
`;

const StyledHighlight = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 4px;
  height: var(--tab-height);
  border-radius: var(--border-radius);
  background: linear-gradient(to bottom, #ff6b6b, var(--green), #ff6b6b);
  box-shadow: 0 0 10px var(--green), 0 0 5px var(--green);
  transform: translateY(calc(${({ activeTabId }) => activeTabId} * var(--tab-height)));
  transition: transform 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);

  @media (max-width: 768px) {
    top: auto;
    bottom: 0;
    width: ${({ styleProps }) => `${styleProps.width}px`};
    height: 2px;
    transform: translateX(${({ styleProps }) => `${styleProps.left}px`});
  }
`;

const StyledTabPanels = styled.div`
  position: relative;
  width: 100%;
  margin-left: 40px;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const StyledTabPanel = styled.div`
  width: 100%;
  height: auto;
  padding: 10px 5px;

  ul {
    ${({ theme }) => theme.mixins.fancyList};

    li {
      &::before {
        color: var(--green);
        text-shadow: 0 0 5px var(--green-tint);
      }
    }

    @media (max-width: 768px) {
      li {
        font-size: 13.5px !important;
        line-height: 1.5 !important;
        margin-bottom: 12px !important;
      }
    }
  }

  h3 {
    margin-bottom: 5px;
    font-size: var(--fz-xxl);
    font-weight: 600;
    color: var(--white);

    .company {
      color: var(--green);
    }

    @media (max-width: 768px) {
      font-size: 18px !important;
      letter-spacing: -0.3px !important;
      line-height: 1.3 !important;
      margin-bottom: 8px !important;
    }
  }

  .range {
    margin-bottom: 25px;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    letter-spacing: 1px;

    @media (max-width: 768px) {
      font-size: 12px !important;
      margin-bottom: 18px !important;
    }
  }
`;

const Jobs = () => {
  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
            }
            html
          }
        }
      }
    }
  `);

  const jobsData = data.jobs.edges;

  const [activeTabId, setActiveTabId] = useState(0);
  const [tabFocus, setTabFocus] = useState(null);
  const [highlightStyle, setHighlightStyle] = useState({ left: 0, width: 0 });
  const tabs = useRef([]);
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (tabs.current[activeTabId]) {
      const tab = tabs.current[activeTabId];
      setHighlightStyle({ left: tab.offsetLeft, width: tab.offsetWidth });
    }
  }, [activeTabId]);

  const focusTab = () => {
    if (tabs.current[tabFocus]) {
      tabs.current[tabFocus].focus();
      return;
    }
    if (tabFocus >= tabs.current.length) {
      setTabFocus(0);
    }
    if (tabFocus < 0) {
      setTabFocus(tabs.current.length - 1);
    }
  };

  useEffect(() => focusTab(), [tabFocus]);

  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ARROW_UP: {
        e.preventDefault();
        setTabFocus(tabFocus - 1);
        break;
      }
      case KEY_CODES.ARROW_DOWN: {
        e.preventDefault();
        setTabFocus(tabFocus + 1);
        break;
      }
      default:
        break;
    }
  };

  return (
    <StyledJobsSection id="jobs" ref={revealContainer}>
      <h2 className="numbered-heading">Professional Journey</h2>

      <div className="inner">
        <StyledTabList role="tablist" aria-label="Job tabs" onKeyDown={e => onKeyDown(e)}>
          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { company } = node.frontmatter;
              return (
                <StyledTabButton
                  key={i}
                  isActive={activeTabId === i}
                  onClick={() => {
                    setActiveTabId(i);
                    const tab = tabs.current[i];
                    setHighlightStyle({ left: tab.offsetLeft, width: tab.offsetWidth });
                  }}
                  ref={el => (tabs.current[i] = el)}
                  id={`tab-${i}`}
                  role="tab"
                  tabIndex={activeTabId === i ? '0' : '-1'}
                  aria-selected={activeTabId === i ? true : false}
                  aria-controls={`panel-${i}`}
                >
                  <span>{company}</span>
                </StyledTabButton>
              );
            })}
          <StyledHighlight activeTabId={activeTabId} styleProps={highlightStyle} />
        </StyledTabList>

        <StyledTabPanels>
          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { frontmatter, html } = node;
              const { title, url, company, range } = frontmatter;

              return (
                <CSSTransition key={i} in={activeTabId === i} timeout={250} classNames="fade">
                  <StyledTabPanel
                    id={`panel-${i}`}
                    role="tabpanel"
                    tabIndex={activeTabId === i ? '0' : '-1'}
                    aria-labelledby={`tab-${i}`}
                    aria-hidden={activeTabId !== i}
                    hidden={activeTabId !== i}
                  >
                    <h3>
                      <span>{title}</span>
                      <span className="company">
                        &nbsp;@&nbsp;
                        <a href={url} className="inline-link">
                          {company}
                        </a>
                      </span>
                    </h3>

                    <p className="range">{range}</p>

                    <div dangerouslySetInnerHTML={{ __html: html }} />
                  </StyledTabPanel>
                </CSSTransition>
              );
            })}
        </StyledTabPanels>
      </div>
    </StyledJobsSection>
  );
};

export default Jobs;
